import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import { SuperAdminService } from '@/services/superadmin.service';
import SuperAdminSidebar from '@/components/sidebar/superAdminSidebar';
import logoImg from '@/assets/Sthitih_Logo.png';
import { NotificationBell } from '@/components/NotificationBell';
import {
  Calendar, CreditCard, Power, Plus, Gem, Check, X, ArrowUpCircle, Gift, RefreshCw, DollarSign, RotateCcw,
  Receipt, Clock, Activity, Zap, HardDrive, ShieldAlert, Mail, AlertCircle, Layers, Ban, Trash2,
  Award, Briefcase, ExternalLink, Users, Search
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ConfirmActionModal } from '@/components/ConfirmActionModal';
import { SuperAdminMobileHeader } from '@/components/navigation/SuperAdminMobileHeader';

interface TenantData {
  id: string;
  name: string;
  subdomain: string;
  logo_url: string | null;
  theme_color: string | null;
  user_count: number;
  event_count: number;
  status: 'active' | 'trial' | 'suspended' | 'expired' | 'pending' | 'revoked';
  plan: string;
  trial_ends_at: string | null;
  discount_applied: number;
  storage_used: number;
  storage_limit: number;
  api_requests_used: number;
  api_requests_limit: number;
  email_quota_used: number;
  email_quota_limit: number;
  onboarding_completed: boolean;
  last_login_at: string | null;
  users: any[];
  events: any[];
  created_at: string;
  createdAt?: string;
  parent_id?: string | null;
  invite_email?: string;
  max_events_limit?: number;
}

interface Invoice {
  id: string;
  client_id: string;
  amount: number;
  tax: number;
  status: 'paid' | 'unpaid' | 'failed' | 'refunded' | 'draft';
  invoice_number: string;
  due_date: string;
  paid_at: string | null;
  payment_method: string | null;
  created_at: string;
}

const statusBadge: Record<string, { bg: string; text: string; border: string; glow: string }> = {
  active: { bg: 'bg-[#DFF5E7]', text: 'text-[#1F7A4F]', border: 'border-[#1F7A4F]/20', glow: '' },
  trial: { bg: 'bg-[#FFF3D8]', text: 'text-[#C47C14]', border: 'border-[#C47C14]/20', glow: '' },
  suspended: { bg: 'bg-[#FCE5E5]', text: 'text-[#C43D3D]', border: 'border-[#C43D3D]/20', glow: '' },
  expired: { bg: 'bg-[#EFE5D6]', text: 'text-[#8A4B22]', border: 'border-[#8A4B22]/20', glow: '' },
};

const invoiceBadge: Record<string, { bg: string; text: string; border: string }> = {
  paid: { bg: 'bg-[#DFF5E7]', text: 'text-[#1F7A4F]', border: 'border-[#1F7A4F]/20' },
  unpaid: { bg: 'bg-[#FFF3D8]', text: 'text-[#C47C14]', border: 'border-[#C47C14]/20' },
  failed: { bg: 'bg-[#FCE5E5]', text: 'text-[#C43D3D]', border: 'border-[#C43D3D]/20' },
  refunded: { bg: 'bg-[#E7F4F0]', text: 'text-[#2E7D68]', border: 'border-[#2E7D68]/20' },
  draft: { bg: 'bg-[#EFE5D6]', text: 'text-[#8A4B22]', border: 'border-[#8A4B22]/20' },
};

const mockTrafficData = [
  { week: 'W1', requests: 3200 },
  { week: 'W2', requests: 4100 },
  { week: 'W3', requests: 3800 },
  { week: 'W4', requests: 5200 },
  { week: 'W5', requests: 4700 },
  { week: 'W6', requests: 6100 },
  { week: 'W7', requests: 5500 },
  { week: 'W8', requests: 7200 },
];

const TenantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState<TenantData | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [allClients, setAllClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Sthitih Mentors State
  const [clientMentors, setClientMentors] = useState<any[]>([]);
  const [allGlobalMentors, setAllGlobalMentors] = useState<any[]>([]);
  const [isAssignMentorModalOpen, setIsAssignMentorModalOpen] = useState(false);
  const [selectedMentorIdsToAssign, setSelectedMentorIdsToAssign] = useState<string[]>([]);
  const [assigningMentors, setAssigningMentors] = useState(false);
  const [mentorAssignSearch, setMentorAssignSearch] = useState('');

  // Modals config
  const [modalType, setModalType] = useState<'plan' | 'trial' | 'discount' | 'status' | 'invoice' | 'parent' | 'limit' | null>(null);
  const [newPlan, setNewPlan] = useState('');
  const [trialDays, setTrialDays] = useState(14);
  const [discountVal, setDiscountVal] = useState(0);
  const [eventLimitVal, setEventLimitVal] = useState(5);
  const [newStatus, setNewStatus] = useState<'active' | 'trial' | 'suspended' | 'expired' | 'pending' | 'revoked'>('active');
  const [newParentId, setNewParentId] = useState<string | null>(null);

  const [invoiceForm, setInvoiceForm] = useState({ amount: '', tax: '0', due_date: '' });
  const [resultModal, setResultModal] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Confirmation modal for delete and revoke
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    actionType: 'delete' | 'revoke';
    targetName: string;
    onConfirm: () => Promise<void>;
  }>({
    isOpen: false,
    actionType: 'delete',
    targetName: '',
    onConfirm: async () => {},
  });

  const fetchData = async (tenantId: string) => {
    try {
      setLoading(true);
      const [tenantData, allInvoices, allPlans, clientsList, cMentors, gMentors] = await Promise.all([
        SuperAdminService.getClientById(tenantId),
        SuperAdminService.getInvoices().catch(() => []),
        SuperAdminService.getPlans().catch(() => []),
        SuperAdminService.getClients().catch(() => []),
        SuperAdminService.getClientSthitihMentors(tenantId).catch(() => []),
        SuperAdminService.getSthitihMentors().catch(() => ({ mentors: [] }))
      ]);
      setTenant(tenantData);
      setPlans(allPlans || []);
      setAllClients((clientsList || []).filter((c: any) => c.id !== tenantId));
      setInvoices((allInvoices || []).filter((inv: Invoice) => inv.client_id === tenantId));
      setClientMentors(cMentors || []);
      setAllGlobalMentors(gMentors?.mentors || []);
    } catch {
      toast.error('Failed to load details');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignMentorsModal = () => {
    const alreadyGrantedIds = new Set(clientMentors.map((m) => m.mentor_id));
    setSelectedMentorIdsToAssign(Array.from(alreadyGrantedIds));
    setMentorAssignSearch('');
    setIsAssignMentorModalOpen(true);
  };

  const handleToggleMentorAssignment = (mId: string) => {
    setSelectedMentorIdsToAssign(prev =>
      prev.includes(mId) ? prev.filter(id => id !== mId) : [...prev, mId]
    );
  };

  const handleSaveAssignedMentors = async () => {
    if (!id) return;
    try {
      setAssigningMentors(true);
      await SuperAdminService.assignMentorsToClient(id, selectedMentorIdsToAssign);
      toast.success('Successfully updated Sthitih Mentors for this institution');
      setIsAssignMentorModalOpen(false);
      const updated = await SuperAdminService.getClientSthitihMentors(id);
      setClientMentors(updated || []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update assigned mentors');
    } finally {
      setAssigningMentors(false);
    }
  };

  const handleRemoveMentor = async (mentorId: string, mentorName: string) => {
    if (!id) return;
    try {
      await SuperAdminService.removeMentorFromClient(id, mentorId);
      toast.success(`Revoked access for ${mentorName}`);
      setClientMentors(prev => prev.filter(m => m.mentor_id !== mentorId));
    } catch (err: any) {
      toast.error('Failed to revoke mentor access');
    }
  };

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id]);

  const handleOpenModal = (type: 'plan' | 'trial' | 'discount' | 'status' | 'invoice' | 'parent' | 'limit') => {
    setModalType(type);
    if (type === 'plan' && tenant) setNewPlan(tenant.plan);
    if (type === 'status' && tenant) setNewStatus(tenant.status);
    if (type === 'discount' && tenant) setDiscountVal(tenant.discount_applied || 0);
    if (type === 'parent' && tenant) setNewParentId(tenant.parent_id || null);
    if (type === 'limit' && tenant) setEventLimitVal((tenant as any).max_events_limit ?? 5);
  };

  const handleCloseModal = () => {
    setModalType(null);
  };

  const handleConfirmAction = async () => {
    if (!tenant) return;
    try {
      let payload: any = {};
      if (modalType === 'plan') payload = { plan: newPlan };
      if (modalType === 'trial') {
        const d = new Date();
        d.setDate(d.getDate() + trialDays);
        payload = { trial_ends_at: d.toISOString(), status: 'trial' };
      }
      if (modalType === 'discount') payload = { discount_applied: discountVal };
      if (modalType === 'status') payload = { status: newStatus };
      if (modalType === 'limit') payload = { max_events_limit: Number(eventLimitVal) };

      if (modalType === 'parent') {
        await SuperAdminService.updateClient(tenant.id, { parent_id: newParentId });
        setResultModal({ type: 'success', message: 'Parent organization hierarchy updated' });
      } else if (modalType === 'invoice') {
        if (!invoiceForm.amount || !invoiceForm.due_date) {
          toast.error('Please input details');
          return;
        }
        await SuperAdminService.createManualInvoice({
          client_id: tenant.id,
          amount: parseFloat(invoiceForm.amount),
          tax: parseFloat(invoiceForm.tax || '0'),
          due_date: invoiceForm.due_date,
        });
        setResultModal({ type: 'success', message: 'Invoice raised successfully' });
      } else {
        await SuperAdminService.updateClient(tenant.id, payload);
        setResultModal({ type: 'success', message: 'Platform changes recorded' });
      }
      setModalType(null);
      fetchData(tenant.id);
    } catch {
      setResultModal({ type: 'error', message: 'Failed to complete configuration changes' });
      setModalType(null);
    }
  };

  const handleRecordPayment = async (invId: string) => {
    if (!tenant) return;
    try {
      await SuperAdminService.payInvoice(invId, 'Manual Entry');
      setResultModal({ type: 'success', message: 'Payment registered successfully' });
      fetchData(tenant.id);
    } catch {
      setResultModal({ type: 'error', message: 'Payment record failed' });
    }
  };

  const handleRefundInvoice = async (invId: string) => {
    if (!tenant) return;
    try {
      await SuperAdminService.refundInvoice(invId);
      setResultModal({ type: 'success', message: 'Refund complete' });
      fetchData(tenant.id);
    } catch {
      setResultModal({ type: 'error', message: 'Refund request failed' });
    }
  };

  const handlePushInvoice = async (invId: string) => {
    if (!tenant) return;
    try {
      await SuperAdminService.pushInvoice(invId);
      setResultModal({ type: 'success', message: 'Invoice pushed & raised successfully' });
      fetchData(tenant.id);
    } catch {
      setResultModal({ type: 'error', message: 'Failed to push invoice' });
    }
  };

  const handleQuickToggleStatus = async () => {
    if (!tenant) return;
    const next = tenant.status === 'active' ? 'suspended' : 'active';
    try {
      await SuperAdminService.updateSubscription(tenant.id, { status: next });
      setResultModal({ type: 'success', message: `Client status changed to ${next}` });
      fetchData(tenant.id);
    } catch {
      setResultModal({ type: 'error', message: 'Status change failed' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F2EA] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#1F5A43]/20 border-t-[#1F5A43] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-[#F7F2EA] flex items-center justify-center">
        <div className="text-center p-8 bg-[#FFFDF9] rounded-[18px] shadow-earth max-w-sm border border-[#E4D8C8]">
          <AlertCircle className="w-12 h-12 text-[#D64545] mx-auto mb-4" />
          <p className="text-sm font-bold text-[#243238]">Tenant Not Found</p>
          <button onClick={() => navigate('/superadmin-dashboard')} className="mt-4 btn-primary text-xs">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F2EA] text-[#243238] flex w-full relative">
      <Toaster position="top-right" />

      <SuperAdminSidebar />

      {/* Main Container */}
      <div className="flex-1 flex flex-col pl-0 pb-24 md:pl-20 md:pb-8 min-h-screen min-w-0 w-full relative">
        <SuperAdminMobileHeader title={tenant.name || 'Client Details'} />
        
        {/* Sleek blended desktop header */}
        <header className="hidden md:flex h-16 px-8 bg-[#F7F2EA] border-b border-[#DDD0BE] items-center justify-between sticky top-0 z-20 w-full">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-bold uppercase px-3.5 py-2 rounded-xl bg-[#DFF5E7] text-[#1F7A4F] border border-[#1F7A4F]/20 tracking-wider shadow-xs">
              Client: {tenant.name}
            </span>
          </div>
          <div className="flex items-center gap-4 select-none">
            <NotificationBell type="superadmin" />
            <img
              src={logoImg}
              alt="Sthitih"
              className="h-10 w-auto object-contain rounded-xl shadow-xs border border-black/5 hover:scale-105 transition-transform"
            />
          </div>
        </header>

        {/* Content Body - Starts directly with content, no page title */}
        <main className="p-4 sm:p-8 max-w-[1600px] mx-auto w-full space-y-8 relative">
          
          {/* Pending or Revoked Invitation Banner */}
          {(tenant.status === 'pending' || tenant.status === 'revoked') && (
            <div className={`p-6 border-2 rounded-[18px] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-earth ${
              tenant.status === 'revoked'
                ? 'bg-rose-50 border-rose-300'
                : 'bg-amber-50 border-amber-300'
            }`}>
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-2xl border flex items-center justify-center shrink-0 ${
                  tenant.status === 'revoked'
                    ? 'bg-rose-100 border-rose-300 text-rose-800'
                    : 'bg-amber-100 border-amber-300 text-amber-800'
                }`}>
                  {tenant.status === 'revoked' ? (
                    <Ban className="w-6 h-6 text-rose-800" />
                  ) : (
                    <Mail className="w-6 h-6 animate-pulse text-amber-800" />
                  )}
                </div>
                <div>
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                    tenant.status === 'revoked'
                      ? 'bg-rose-200 text-rose-900 border-rose-400'
                      : 'bg-amber-200 text-amber-900 border-amber-400'
                  }`}>
                    {tenant.status === 'revoked' ? 'Invitation Revoked' : 'Pending Invitation'}
                  </span>
                  <h3 className="text-base font-black text-slate-900 mt-1">
                    {tenant.status === 'revoked'
                      ? 'Invitation Link Has Been Revoked & Deactivated'
                      : 'Awaiting Organization Administrator Acceptance & Registration'}
                  </h3>
                  <p className="text-xs text-slate-600 font-semibold mt-0.5">
                    Invitation sent to <strong className={tenant.status === 'revoked' ? 'text-rose-900' : 'text-amber-900'}>{tenant.invite_email || 'client'}</strong> on {new Date(tenant.created_at || new Date()).toLocaleDateString()}.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await SuperAdminService.inviteClient(tenant.name, tenant.invite_email || '', (tenant as any).invite_message || null);
                      toast.success(`Resent invitation email to ${tenant.invite_email}`);
                      if (id) fetchData(id);
                    } catch {
                      toast.error('Failed to resend invitation');
                    }
                  }}
                  className="px-3.5 py-2 rounded-xl bg-[#1F5A43] hover:bg-[#143d2d] text-white text-xs font-bold transition-all shadow-xs cursor-pointer"
                >
                  {tenant.status === 'revoked' ? 'Re-issue Invite' : 'Resend Invite Email'}
                </button>

                {tenant.status !== 'revoked' && (
                  <button
                    type="button"
                    onClick={() => {
                      setConfirmModal({
                        isOpen: true,
                        actionType: 'revoke',
                        targetName: tenant.name,
                        onConfirm: async () => {
                          try {
                            await SuperAdminService.revokeInvitation(tenant.id);
                            toast.success(`Invitation for ${tenant.name} revoked`);
                            if (id) fetchData(id);
                          } catch {
                            toast.error('Failed to revoke invitation');
                          }
                        }
                      });
                    }}
                    className="px-3.5 py-2 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    <Ban className="w-3.5 h-3.5" />
                    <span>Revoke Invite</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setConfirmModal({
                      isOpen: true,
                      actionType: 'delete',
                      targetName: tenant.name,
                      onConfirm: async () => {
                        try {
                          await SuperAdminService.deleteInvitation(tenant.id);
                          toast.success(`Invitation for ${tenant.name} deleted permanently`);
                          navigate('/events');
                        } catch {
                          toast.error('Failed to delete invitation');
                        }
                      }
                    });
                  }}
                  className="px-3.5 py-2 rounded-xl bg-rose-100 hover:bg-rose-200 text-rose-900 border border-rose-300 text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Delete Invite</span>
                </button>
              </div>
            </div>
          )}

          {/* Section: Subscription Settings */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Left Big Card: Subscription Details & Interactive Controls */}
            <div className="lg:col-span-2 bg-[#FFFDF9] border border-[#E4D8C8] rounded-[18px] p-8 shadow-earth flex flex-col justify-between hover:shadow-earth-lg transition-all duration-300">
              <div>
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#E4D8C8]">
                  <h3 className="text-xs font-bold text-[#4A5558] uppercase tracking-widest flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-[#1F5A43]" />
                    <span>Subscription Settings</span>
                  </h3>
                  <span className="text-[10px] font-bold text-[#8D8A83] font-mono">ID: {(tenant.id || '').slice(0, 8)}</span>
                </div>

                {/* Info grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="bg-[#F7F2EA] p-5 rounded-2xl border border-[#DDD0BE]/60 relative overflow-hidden group">
                    <span className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-wider block mb-1">Plan Tier</span>
                    <div className="flex items-center gap-2">
                      <Gem className="w-4 h-4 text-[#1F5A43]" />
                      <p className="text-sm font-bold text-[#243238] capitalize tracking-tight">{tenant.plan}</p>
                    </div>
                  </div>

                  <div className="bg-[#F7F2EA] p-5 rounded-2xl border border-[#DDD0BE]/60 relative overflow-hidden group">
                    <span className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-wider block mb-1">Contract Status</span>
                    <span className={`inline-block px-3 py-1 rounded-full text-[9px] font-bold border tracking-wider ${statusBadge[tenant.status]?.bg} ${statusBadge[tenant.status]?.text} ${statusBadge[tenant.status]?.border}`}>
                      {tenant.status.toUpperCase()}
                    </span>
                  </div>

                  <div className="bg-[#F7F2EA] p-5 rounded-2xl border border-[#DDD0BE]/60 relative overflow-hidden group">
                    <span className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-wider block mb-1">Discount Active</span>
                    <div className="flex items-center gap-2">
                      <Gift className="w-4 h-4 text-[#8A4B22]" />
                      <p className="text-sm font-bold text-[#243238] tracking-tight">
                        {tenant.discount_applied > 0 ? `${tenant.discount_applied}% Lifetime Off` : 'Standard Price (0%)'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-[#F7F2EA] p-5 rounded-2xl border border-[#DDD0BE]/60 relative overflow-hidden group">
                    <span className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-wider block mb-1">Access Expiration</span>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#8A4B22]" />
                      <p className="text-sm font-bold text-[#243238] tracking-tight">
                        {tenant.trial_ends_at ? new Date(tenant.trial_ends_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'Continuous Contract'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-[#F7F2EA] p-5 rounded-2xl border border-[#DDD0BE]/60 relative overflow-hidden group">
                    <span className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-wider block mb-1">Hierarchy Link</span>
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-[#8A4B22]" />
                      <p className="text-sm font-bold text-[#243238] tracking-tight truncate max-w-[150px]">
                        {tenant.parent_id 
                          ? (allClients.find(c => c.id === tenant.parent_id)?.name || 'Linked Sub-Branch')
                          : 'Independent Entity'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-[#F7F2EA] p-5 rounded-2xl border border-[#DDD0BE]/60 relative overflow-hidden group">
                    <span className="text-[9px] font-bold text-[#8D8A83] uppercase tracking-wider block mb-1">Max Events Creation Limit</span>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#1F5A43]" />
                      <p className="text-sm font-bold text-[#243238] tracking-tight">
                        {(tenant as any).max_events_limit === -1 || ((tenant as any).max_events_limit || 5) >= 999 
                          ? 'Unlimited Events' 
                          : `${(tenant as any).max_events_limit || 5} Event(s) Allowed`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Operations Toolbar */}
              <div className="mt-8 pt-6 border-t border-[#E4D8C8]">
                <span className="text-[9px] font-bold text-[#4A5558] uppercase tracking-widest block mb-3.5">Subscription Actions</span>
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                  <button
                    onClick={() => handleOpenModal('plan')}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-[#F3ECE3] border border-[#D9CDBD] hover:bg-[#E6DCD0] transition-all text-[#5A4634] group font-semibold"
                  >
                    <ArrowUpCircle className="w-5 h-5 mb-1.5 text-[#8D8A83] group-hover:text-[#1F5A43] transition-colors" />
                    <span className="text-[10px] font-bold">Change Tier</span>
                  </button>

                  <button
                    onClick={() => handleOpenModal('limit')}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-[#F3ECE3] border border-[#D9CDBD] hover:bg-[#E6DCD0] transition-all text-[#5A4634] group font-semibold"
                  >
                    <Calendar className="w-5 h-5 mb-1.5 text-[#8D8A83] group-hover:text-[#1F5A43] transition-colors" />
                    <span className="text-[10px] font-bold">Limit Events</span>
                  </button>

                  <button
                    onClick={() => handleOpenModal('trial')}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-[#F3ECE3] border border-[#D9CDBD] hover:bg-[#E6DCD0] transition-all text-[#5A4634] group font-semibold"
                  >
                    <Clock className="w-5 h-5 mb-1.5 text-[#8D8A83] group-hover:text-[#1F5A43] transition-colors" />
                    <span className="text-[10px] font-bold">Extend Trial</span>
                  </button>

                  <button
                    onClick={() => handleOpenModal('discount')}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-[#F3ECE3] border border-[#D9CDBD] hover:bg-[#E6DCD0] transition-all text-[#5A4634] group font-semibold"
                  >
                    <Gift className="w-5 h-5 mb-1.5 text-[#8D8A83] group-hover:text-[#8A4B22] transition-colors" />
                    <span className="text-[10px] font-bold">Apply Discount</span>
                  </button>

                  <button
                    onClick={() => handleOpenModal('status')}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-[#F3ECE3] border border-[#D9CDBD] hover:bg-[#E6DCD0] transition-all text-[#5A4634] group font-semibold"
                  >
                    <RefreshCw className="w-5 h-5 mb-1.5 text-[#8D8A83] group-hover:text-[#1F5A43] transition-colors" />
                    <span className="text-[10px] font-bold">Change Status</span>
                  </button>

                  <button
                    onClick={() => handleOpenModal('parent')}
                    className="flex flex-col items-center justify-center p-3 rounded-xl bg-[#F3ECE3] border border-[#D9CDBD] hover:bg-[#E6DCD0] transition-all text-[#5A4634] group font-semibold"
                  >
                    <Layers className="w-5 h-5 mb-1.5 text-[#8D8A83] group-hover:text-[#1F5A43] transition-colors" />
                    <span className="text-[10px] font-bold">Set Parent</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Right Card: Quick Contract State Toggle Card */}
            <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-[18px] p-8 shadow-earth flex flex-col justify-start space-y-6 hover:shadow-earth-lg transition-all duration-300">
              <div>
                <h3 className="text-xs font-bold text-[#4A5558] uppercase tracking-widest mb-6 pb-4 border-b border-[#E4D8C8] flex items-center gap-2">
                  <Activity className="w-4 h-4 text-[#1F5A43]" />
                  <span>Quick Management</span>
                </h3>
                
                <div className="bg-[#EFE5D6]/40 p-5 rounded-2xl border border-[#DDD0BE] flex items-start gap-3">
                  <div className="mt-0.5 p-2 rounded-xl bg-[#FFF3D8] text-[#C47C14] border border-[#C47C14]/20">
                    <ShieldAlert className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-[#243238] uppercase tracking-wider mb-1">Contract Lifecycle Status</h4>
                    <p className="text-[10px] text-[#6F706C] leading-relaxed">
                      Suspension halts workspace portal operations instantly. Re-activating will enable portal logins immediately.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <button
                  onClick={handleQuickToggleStatus}
                  className={`w-full py-3.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-2 ${
                    tenant.status === 'active'
                      ? 'btn-destructive'
                      : 'btn-primary'
                  }`}
                >
                  <Power className="w-4 h-4" />
                  {tenant.status === 'active' ? 'Suspend Portal Access' : 'Re-Activate Access'}
                </button>
              </div>
            </div>
          </div>

          {/* Section: Resource Quotas Progress Dashboard */}
          <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-[18px] p-8 shadow-earth hover:shadow-earth-lg transition-all duration-300">
            <h3 className="text-xs font-bold text-[#4A5558] uppercase tracking-widest mb-6 pb-4 border-b border-[#E4D8C8] flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#8A4B22]" />
              <span>Resource Quotas & Limits</span>
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { label: 'Storage Allocation', used: tenant.storage_used ?? 0, limit: tenant.storage_limit ?? (5 * 1073741824), unit: 'GB', divisor: 1073741824, icon: HardDrive },
                { label: 'API Quota Access', used: tenant.api_requests_used ?? 0, limit: tenant.api_requests_limit ?? 50000, unit: 'reqs', divisor: 1, icon: Activity },
                { label: 'Mail Quota Limits', used: tenant.email_quota_used ?? 0, limit: tenant.email_quota_limit ?? 5000, unit: 'mails', divisor: 1, icon: Mail },
              ].map((quota) => {
                const used = quota.used ?? 0;
                const limit = quota.limit ?? 0;
                const usedDisplay = quota.divisor > 1 ? (used / quota.divisor).toFixed(2) : used.toLocaleString();
                const limitDisplay = quota.divisor > 1 ? (limit / quota.divisor).toFixed(2) : limit.toLocaleString();
                const pct = limit > 0 ? Math.min(Math.round((used / limit) * 100), 100) : 0;
                const Icon = quota.icon;

                return (
                  <div key={quota.label} className="p-5 rounded-2xl bg-[#F7F2EA] border border-[#DDD0BE]/60 relative overflow-hidden group">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <span className="text-[10px] font-bold text-[#8D8A83] uppercase tracking-wider block">{quota.label}</span>
                        <h4 className="text-sm font-bold text-[#243238] mt-0.5">{usedDisplay} <span className="text-[10px] font-bold text-[#6F706C]">/ {limitDisplay} {quota.unit}</span></h4>
                      </div>
                      <div className="p-2 rounded-xl bg-[#FFFDF9] border border-[#E4D8C8] text-[#1F5A43]">
                        <Icon className="w-4 h-4" />
                      </div>
                    </div>

                    <div className="relative w-full h-2.5 bg-[#DDD0BE]/50 rounded-full overflow-hidden mb-1.5">
                      <div className="h-full bg-[#1F5A43] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between text-[9px] font-bold">
                      <span className="text-[#8D8A83]">Utilization</span>
                      <span className="text-[#243238]">{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section: Sthitih Mentors (Global Industry Mentors Granted) */}
          <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-[18px] p-8 shadow-earth hover:shadow-earth-lg transition-all duration-300">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-[#E4D8C8]">
              <div>
                <h3 className="text-xs font-bold text-[#4A5558] uppercase tracking-widest flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#1F5A43]" />
                  <span>Sthitih Mentors Access ({clientMentors.length})</span>
                </h3>
                <p className="text-[11px] text-[#6F706C] mt-0.5">
                  Platform industry mentors granted to this institution for event team allocation.
                </p>
              </div>

              <button
                onClick={handleOpenAssignMentorsModal}
                className="btn-primary text-xs flex items-center gap-1.5 shrink-0"
              >
                <Plus className="w-3.5 h-3.5" /> Assign Sthitih Mentors
              </button>
            </div>

            {clientMentors.length === 0 ? (
              <div className="p-8 rounded-2xl bg-[#F7F2EA] border border-dashed border-[#DDD0BE] text-center">
                <Award className="w-10 h-10 text-[#DDD0BE] mx-auto mb-2" />
                <h4 className="text-xs font-bold text-[#243238]">No Sthitih Mentors Granted</h4>
                <p className="text-[11px] text-[#6F706C] mt-0.5 max-w-sm mx-auto">
                  This institution has not been granted access to any global Sthitih mentors yet. Assign mentors to let event coordinators allocate teams to them.
                </p>
                <button
                  onClick={handleOpenAssignMentorsModal}
                  className="mt-3.5 px-4 py-2 rounded-xl bg-[#1F5A43] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
                >
                  <Plus className="w-3.5 h-3.5" /> Assign Mentors Now
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {clientMentors.map((grant) => {
                  const m = grant.mentor || {};
                  return (
                    <div
                      key={grant.id}
                      className="p-5 rounded-2xl bg-[#F7F2EA] border border-[#DDD0BE] hover:border-[#1F5A43]/40 transition-all flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex items-center gap-3">
                            {m.avatar_url ? (
                              <img
                                src={m.avatar_url}
                                alt={m.display_name}
                                className="w-11 h-11 rounded-xl object-cover border border-[#DDD0BE]"
                              />
                            ) : (
                              <div className="w-11 h-11 rounded-xl bg-[#1F5A43]/10 text-[#1F5A43] font-bold text-sm flex items-center justify-center border border-[#1F5A43]/20">
                                {(m.display_name || m.email || 'M').charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div>
                              <h4 className="text-xs font-bold text-[#243238]">{m.display_name}</h4>
                              <p className="text-[11px] font-semibold text-[#1F5A43]">
                                {m.designation || 'Industry Advisor'} {m.company ? `• ${m.company}` : ''}
                              </p>
                              <p className="text-[10px] text-[#8D8A83]">{m.email}</p>
                            </div>
                          </div>
                        </div>

                        {((m.mentorship_areas && m.mentorship_areas.length > 0) || (m.skills && m.skills.length > 0)) && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {[...(m.mentorship_areas || []), ...(m.skills || [])].slice(0, 3).map((tag: string, idx: number) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 rounded-md text-[9px] font-medium bg-[#FFFDF9] text-[#5A4634] border border-[#DDD0BE]"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="pt-2 border-t border-[#DDD0BE]/60 flex items-center justify-between text-[10px]">
                          <span className="font-semibold text-[#6F706C]">
                            {grant.allocated_teams_in_client ?? 0} team(s) allocated
                          </span>
                          <span className="text-[#8D8A83]">
                            Granted {new Date(grant.assigned_at || grant.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t border-[#DDD0BE]/60 flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveMentor(grant.mentor_id, m.display_name || 'Mentor')}
                          className="px-2.5 py-1 rounded-lg text-[10px] font-bold text-[#C43D3D] hover:bg-rose-50 border border-transparent hover:border-rose-200 transition-colors"
                        >
                          Revoke Access
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Section: Invoices & Billing Ledger Ledger */}
          <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-[18px] p-8 shadow-earth hover:shadow-earth-lg transition-all duration-300">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-[#E4D8C8]">
              <h3 className="text-xs font-bold text-[#4A5558] uppercase tracking-widest flex items-center gap-2">
                <Receipt className="w-4 h-4 text-[#8A4B22]" />
                <span>Billing Invoices Ledger</span>
              </h3>
              <button
                onClick={() => handleOpenModal('invoice')}
                className="btn-primary text-xs flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> Raise Invoice
              </button>
            </div>

            {invoices.length === 0 ? (
              <div className="text-center py-12 text-[#6F706C] text-xs font-bold bg-[#F7F2EA] border border-dashed border-[#DDD0BE] rounded-2xl max-w-md mx-auto">
                No invoices registered under this account ledger.
              </div>
            ) : (
              <div className="space-y-8">
                {/* Draft Bills (Upcoming / Ready to Raise) */}
                <div>
                  <h4 className="text-[10px] font-bold text-[#4A5558] uppercase tracking-widest mb-4">Draft Bills (Upcoming / Ready to Raise)</h4>
                  {invoices.filter(inv => inv.status === 'draft').length === 0 ? (
                    <p className="text-xs text-[#6F706C] italic pl-1">No pending draft bills. Upcoming invoices are automatically initialized.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {invoices.filter(inv => inv.status === 'draft').map((inv) => (
                        <div
                          key={inv.id}
                          className="rounded-[18px] border border-[#E4D8C8] p-6 bg-[#F7F2EA]/60 shadow-earth hover:border-[#1F5A43] transition-all duration-200 flex flex-col justify-between"
                        >
                          <div>
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <span className="text-[9px] font-mono text-[#8D8A83] uppercase tracking-wider block">{inv.invoice_number}</span>
                                <span className="text-[10px] text-[#6F706C] font-semibold block mt-0.5">{new Date(inv.created_at).toLocaleDateString()}</span>
                              </div>
                              <span className="px-2.5 py-0.5 rounded-full text-[9px] font-bold border uppercase tracking-wider bg-[#EFE5D6] text-[#8A4B22] border-[#8A4B22]/20">
                                DRAFT
                              </span>
                            </div>

                            <div className="space-y-2 mt-4 pt-3 border-t border-[#DDD0BE]/60 text-xs font-semibold text-[#4A5558]">
                              <div className="flex justify-between">
                                <span>Base Amount</span>
                                <span className="font-bold text-[#243238]">₹{Number(inv.amount || 0).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Applicable Tax</span>
                                <span className="font-medium text-[#6F706C]">₹{Number(inv.tax || 0).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Due Date</span>
                                <span className="font-medium text-[#6F706C]">{new Date(inv.due_date).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-6 pt-4 border-t border-[#DDD0BE]/60">
                            <button
                              onClick={() => handlePushInvoice(inv.id)}
                              className="w-full btn-primary text-xs flex items-center justify-center gap-1.5"
                            >
                              <Zap className="w-3.5 h-3.5" /> Push & Raise Invoice
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Issued Invoices (Outstanding / Paid) */}
                <div>
                  <h4 className="text-[10px] font-bold text-[#4A5558] uppercase tracking-widest mb-4">Issued Invoices & Payments Ledger</h4>
                  {invoices.filter(inv => inv.status !== 'draft').length === 0 ? (
                    <p className="text-xs text-[#6F706C] italic pl-1">No issued invoices recorded yet.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {invoices.filter(inv => inv.status !== 'draft').map((inv) => (
                        <div
                          key={inv.id}
                          className="rounded-[18px] border border-[#E4D8C8] p-6 bg-[#FFFDF9] shadow-earth hover:border-[#1F5A43] transition-all duration-200 flex flex-col justify-between"
                        >
                          <div>
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <span className="text-[9px] font-mono text-[#8D8A83] uppercase tracking-wider block">{inv.invoice_number}</span>
                                <span className="text-[10px] text-[#6F706C] font-semibold block mt-0.5">{new Date(inv.created_at).toLocaleDateString()}</span>
                              </div>
                              <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-bold border uppercase tracking-wider ${invoiceBadge[inv.status]?.bg} ${invoiceBadge[inv.status]?.text} ${invoiceBadge[inv.status]?.border}`}>
                                {inv.status}
                              </span>
                            </div>

                            <div className="space-y-2 mt-4 pt-3 border-t border-[#E4D8C8] text-xs font-semibold text-[#4A5558]">
                              <div className="flex justify-between">
                                <span>Base Amount</span>
                                <span className="font-bold text-[#243238]">₹{Number(inv.amount || 0).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Applicable Tax</span>
                                <span className="font-medium text-[#6F706C]">₹{Number(inv.tax || 0).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Due Date</span>
                                <span className="font-medium text-[#6F706C]">{new Date(inv.due_date).toLocaleDateString()}</span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-6 pt-4 border-t border-[#E4D8C8]">
                            {inv.status === 'unpaid' && (
                              <button
                                onClick={() => handleRecordPayment(inv.id)}
                                className="w-full btn-primary text-xs flex items-center justify-center gap-1.5"
                              >
                                <DollarSign className="w-3.5 h-3.5" /> Record Payment
                              </button>
                            )}
                            {inv.status === 'paid' && (
                              <button
                                onClick={() => handleRefundInvoice(inv.id)}
                                className="w-full btn-secondary text-xs flex items-center justify-center gap-1.5"
                              >
                                <RotateCcw className="w-3.5 h-3.5" /> Request Refund
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Section: Platform Traffic Metrics */}
          <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-[18px] p-8 shadow-earth hover:shadow-earth-lg transition-all duration-300">
            <h3 className="text-xs font-bold text-[#4A5558] uppercase tracking-widest mb-6 pb-4 border-b border-[#E4D8C8] flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#1F5A43]" />
              <span>Platform Traffic & Network Access</span>
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={mockTrafficData}>
                  <defs>
                    <linearGradient id="glowColorGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={tenant.theme_color || '#1F5A43'} stopOpacity={0.25}/>
                      <stop offset="95%" stopColor={tenant.theme_color || '#1F5A43'} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#DDD0BE" opacity={0.3} />
                  <XAxis dataKey="week" stroke="#8D8A83" fontSize={11} tickLine={false} />
                  <YAxis stroke="#8D8A83" fontSize={11} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #E4D8C8', backgroundColor: '#FFFDF9', boxShadow: '0 8px 24px rgba(73,52,33,0.08)' }} />
                  <Area type="monotone" dataKey="requests" stroke={tenant.theme_color || '#1F5A43'} strokeWidth={3} fillOpacity={1} fill="url(#glowColorGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Section: Client Health metrics */}
          <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-[18px] p-8 shadow-earth hover:shadow-earth-lg transition-all duration-300">
            <h3 className="text-xs font-bold text-[#4A5558] uppercase tracking-widest mb-6 pb-4 border-b border-[#E4D8C8] flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#1F5A43]" />
              <span>Workspace Health Profile</span>
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="bg-[#DFF5E7]/50 p-5 rounded-2xl border border-[#1F7A4F]/20">
                <span className="text-[10px] font-bold text-[#1F7A4F] uppercase tracking-wider block">Health Status</span>
                <p className="text-lg font-bold text-[#1F7A4F] mt-1.5 capitalize">{tenant.status}</p>
              </div>
              <div className="bg-[#E7F4F0] p-5 rounded-2xl border border-[#2E7D68]/20">
                <span className="text-[10px] font-bold text-[#2E7D68] uppercase tracking-wider block">Licensed Seats</span>
                <p className="text-lg font-bold text-[#2E7D68] mt-1.5">{tenant.user_count}</p>
              </div>
              <div className="bg-[#EFE5D6]/60 p-5 rounded-2xl border border-[#8A4B22]/20">
                <span className="text-[10px] font-bold text-[#8A4B22] uppercase tracking-wider block">Onboarding Progress</span>
                <p className="text-lg font-bold text-[#8A4B22] mt-1.5">{tenant.onboarding_completed ? 'Completed' : 'Pending'}</p>
              </div>
              <div className="bg-[#FFF3D8] p-5 rounded-2xl border border-[#C47C14]/20">
                <span className="text-[10px] font-bold text-[#C47C14] uppercase tracking-wider block">Last Active</span>
                <p className="text-xs font-bold text-[#C47C14] mt-1.5">
                  {tenant.last_login_at ? new Date(tenant.last_login_at).toLocaleDateString() : 'No activity logged'}
                </p>
              </div>
            </div>
          </div>


          {/* Section: Client Events */}
          {tenant.events && tenant.events.length > 0 && (
            <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-[18px] p-8 shadow-earth hover:shadow-earth-lg transition-all duration-300">
              <h3 className="text-xs font-bold text-[#4A5558] uppercase tracking-widest mb-6 pb-4 border-b border-[#E4D8C8] flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#8A4B22]" />
                <span>Configured Events</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {tenant.events.map((event: any) => (
                  <div key={event.id} className="bg-[#FFFDF9] p-5 rounded-2xl border border-[#E4D8C8] hover:border-[#1F5A43] transition-all flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className={`text-[9px] font-bold uppercase px-2.5 py-0.5 rounded-full border ${event.type === 'hackathon' ? 'bg-[#EFE5D6] text-[#8A4B22] border-[#8A4B22]/20' : 'bg-[#DFF5E7] text-[#1F7A4F] border-[#1F7A4F]/20'}`}>
                          {event.type}
                        </span>
                        <span className="text-[10px] text-[#6F706C] font-semibold">
                          {new Date(event.start_date).toLocaleDateString()}
                        </span>
                      </div>
                      <h4 className="text-xs font-bold text-[#243238] line-clamp-1">{event.name}</h4>
                      <p className="text-[10px] text-[#6F706C] line-clamp-2 mt-1 leading-relaxed">{event.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Subscription configuration Modals */}
      <AnimatePresence>
        {modalType && modalType !== 'invoice' && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000]"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#FFFDF9] rounded-[18px] shadow-earth p-8 max-w-md w-full mx-4 border border-[#E4D8C8]"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xs font-bold text-[#243238] uppercase tracking-widest mb-1.5">
                {modalType === 'plan' && 'Change Plan Option'}
                {modalType === 'limit' && 'Configure Custom Event Creation Limit'}
                {modalType === 'trial' && 'Extend Trial Duration'}
                {modalType === 'discount' && 'Configure Discount'}
                {modalType === 'status' && 'Update Platform Status'}
                {modalType === 'parent' && 'Associate Parent Organization'}
              </h2>
              <p className="text-[10px] text-[#6F706C] font-semibold mb-6">Modifying settings for: <strong className="text-[#243238] font-bold">{tenant.name}</strong></p>

              {modalType === 'plan' && (
                <div className="space-y-3">
                  {plans.map((p) => (
                    <label key={p.id} className={`flex items-center gap-3.5 p-4 rounded-2xl border-2 cursor-pointer transition-all ${newPlan === p.id ? 'border-[#1F5A43] bg-[#DFF5E7]/40' : 'border-[#E4D8C8] hover:border-[#DDD0BE]'}`}>
                      <input
                        type="radio"
                        name="plan"
                        value={p.id}
                        checked={newPlan === p.id}
                        onChange={() => setNewPlan(p.id)}
                        className="accent-[#1F5A43]"
                      />
                      <div className="flex flex-col">
                        <span className="font-bold text-xs text-[#243238] capitalize">{p.name}</span>
                        <span className="text-[9px] font-semibold text-[#6F706C] mt-0.5">₹{parseFloat(p.price).toLocaleString()} / month</span>
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {modalType === 'trial' && (
                <div>
                  <label className="block text-[10px] font-bold text-[#4A5558] uppercase tracking-wider mb-2">Extension Days</label>
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={trialDays}
                    onChange={(e) => setTrialDays(Number(e.target.value))}
                    className="w-full px-4 py-3 border border-[#DCCFBE] rounded-xl focus:ring-2 focus:ring-[#1F5A43] outline-none text-xs text-[#243238] font-bold bg-white"
                  />
                </div>
              )}

              {modalType === 'limit' && (
                <div>
                  <label className="block text-[10px] font-bold text-[#4A5558] uppercase tracking-wider mb-2">Maximum Event Creation Limit</label>
                  <input
                    type="number"
                    min={1}
                    max={999}
                    value={eventLimitVal}
                    onChange={(e) => setEventLimitVal(Number(e.target.value))}
                    className="w-full px-4 py-3 border border-[#DCCFBE] rounded-xl focus:ring-2 focus:ring-[#1F5A43] outline-none text-xs text-[#243238] font-bold bg-white"
                    placeholder="e.g. 5 (or 999 for unlimited)"
                  />
                  <p className="text-[10px] text-slate-500 font-medium mt-2 leading-relaxed">
                    Sets the maximum number of events this organization can create. When reached, users clicking "+ New Event" will see an upgrade request modal.
                  </p>
                </div>
              )}

              {modalType === 'discount' && (
                <div>
                  <label className="block text-[10px] font-bold text-[#4A5558] uppercase tracking-wider mb-2">Lifetime Discount Percentage (%)</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={discountVal}
                    onChange={(e) => setDiscountVal(Number(e.target.value))}
                    className="w-full px-4 py-3 border border-[#DCCFBE] rounded-lg focus:ring-2 focus:ring-[#1F5A43]/20 outline-none text-xs text-[#243238] font-bold bg-[#FFFDF9]"
                  />
                </div>
              )}

              {modalType === 'status' && (
                <div className="space-y-3">
                  {(['active', 'trial', 'suspended', 'expired'] as const).map((s) => (
                    <label key={s} className={`flex items-center gap-3.5 p-4 rounded-xl border-2 cursor-pointer transition-all ${newStatus === s ? 'border-[#1F5A43] bg-[#E8F5E9]' : 'border-[#E4D8C8] hover:border-[#1F5A43]/50'}`}>
                      <input
                        type="radio"
                        name="status"
                        value={s}
                        checked={newStatus === s}
                        onChange={() => setNewStatus(s)}
                        className="accent-[#1F5A43]"
                      />
                      <span className="font-bold text-xs text-[#243238] capitalize">{s}</span>
                    </label>
                  ))}
                </div>
              )}

              {modalType === 'parent' && (
                <div className="space-y-4">
                  <label className="block text-[10px] font-bold text-[#4A5558] uppercase tracking-wider mb-2">Select Parent Node</label>
                  <select
                    value={newParentId || ''}
                    onChange={(e) => setNewParentId(e.target.value || null)}
                    className="w-full px-4 py-3 border border-[#DCCFBE] rounded-xl focus:ring-2 focus:ring-[#1F5A43] outline-none text-xs text-[#243238] font-bold bg-white"
                  >
                    <option value="">Independent Entity (No Parent)</option>
                    {allClients.map((client) => (
                      <option key={client.id} value={client.id}>{client.name} ({client.subdomain})</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-slate-400 font-semibold leading-normal mt-1">
                    Setting a parent groups this college under the parent node's organizational structure system.
                  </p>
                </div>
              )}

              <div className="flex gap-3 mt-8">
                <button onClick={handleCloseModal} className="flex-1 px-4 py-3 bg-[#F0EEEB] hover:bg-[#E4D8C8] text-[#4A5558] rounded-xl text-xs font-bold transition-colors">Cancel</button>
                <button onClick={handleConfirmAction} className="flex-1 px-4 py-3 bg-[#243238] hover:bg-[#1F5A43] text-white rounded-xl text-xs font-bold transition-colors">Confirm Changes</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manual invoice Modal */}
      <AnimatePresence>
        {modalType === 'invoice' && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#243238]/60 backdrop-blur-sm flex items-center justify-center z-[1000]"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full mx-4 border border-[#DCCFBE]"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-sm font-extrabold text-[#243238] uppercase tracking-widest mb-6">Raise Manual Invoice</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-[#4A5558] uppercase tracking-wider mb-2">Base Invoice Amount (₹)</label>
                  <input
                    type="number"
                    value={invoiceForm.amount}
                    onChange={e => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                    className="w-full px-4 py-3 border border-[#DCCFBE] rounded-lg focus:ring-2 focus:ring-[#1F5A43]/20 outline-none text-xs text-[#243238] font-bold bg-[#FFFDF9]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#4A5558] uppercase tracking-wider mb-2">Tax Rate amount (₹)</label>
                  <input
                    type="number"
                    value={invoiceForm.tax}
                    onChange={e => setInvoiceForm({ ...invoiceForm, tax: e.target.value })}
                    className="w-full px-4 py-3 border border-[#DCCFBE] rounded-lg focus:ring-2 focus:ring-[#1F5A43]/20 outline-none text-xs text-[#243238] font-bold bg-[#FFFDF9]"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-[#4A5558] uppercase tracking-wider mb-2">Invoice Due Expiry</label>
                  <input
                    type="date"
                    value={invoiceForm.due_date}
                    onChange={e => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })}
                    className="w-full px-4 py-3 border border-[#DCCFBE] rounded-lg focus:ring-2 focus:ring-[#1F5A43]/20 outline-none text-xs text-[#243238] font-bold bg-[#FFFDF9]"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-8">
                <button onClick={handleCloseModal} className="flex-1 px-4 py-3 bg-[#F0EEEB] hover:bg-[#E4D8C8] text-[#4A5558] rounded-xl text-xs font-bold transition-colors">Cancel</button>
                <button onClick={handleConfirmAction} className="flex-1 px-4 py-3 bg-[#243238] hover:bg-[#1F5A43] text-white rounded-xl text-xs font-bold transition-colors">Create Invoice</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation result alert box */}
      <AnimatePresence>
        {resultModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#243238]/60 backdrop-blur-sm flex items-center justify-center z-[1001]"
            onClick={() => setResultModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center border border-[#DCCFBE]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 ${resultModal.type === 'success' ? 'bg-[#E8F5E9] text-[#1F7A4F]' : 'bg-[#FCE5E5] text-[#C43D3D]'}`}>
                {resultModal.type === 'success' ? <Check className="w-8 h-8" /> : <X className="w-8 h-8" />}
              </div>
              <h3 className="text-base font-extrabold text-[#243238] uppercase tracking-widest mb-2">{resultModal.type === 'success' ? 'Task Success' : 'Error'}</h3>
              <p className="text-sm text-[#6F706C] font-medium mb-8">{resultModal.message}</p>
              <button onClick={() => setResultModal(null)} className="w-full px-6 py-3.5 bg-[#243238] hover:bg-[#1F5A43] text-white rounded-xl text-xs font-bold transition-colors">Close Dialog</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal: Assign Sthitih Mentors to Institution */}
      {isAssignMentorModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-[#FFFDF9] border border-[#E4D8C8] rounded-2xl max-w-xl w-full p-6 shadow-earth-lg space-y-5 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-3 border-b border-[#E4D8C8]">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-[#1F5A43]/10 text-[#1F5A43]">
                  <Award className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#243238]">Assign Sthitih Mentors</h3>
                  <p className="text-[11px] text-[#6F706C]">
                    Select which global mentors are accessible to {tenant?.name}.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsAssignMentorModalOpen(false)}
                className="p-1.5 rounded-lg text-[#8D8A83] hover:text-[#243238] hover:bg-[#F7F2EA]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#8D8A83]" />
                <input
                  type="text"
                  value={mentorAssignSearch}
                  onChange={(e) => setMentorAssignSearch(e.target.value)}
                  placeholder="Search mentors by name, company, or domain..."
                  className="w-full pl-9 pr-4 py-2 bg-[#F7F2EA] border border-[#DDD0BE] rounded-xl text-xs text-[#243238] placeholder-[#8D8A83] focus:outline-none focus:border-[#1F5A43]"
                />
              </div>

              <div className="max-h-64 overflow-y-auto border border-[#DDD0BE] rounded-xl p-2 bg-[#F7F2EA] space-y-1.5">
                {allGlobalMentors.length === 0 ? (
                  <div className="p-4 text-center text-xs text-[#8D8A83]">
                    No Sthitih mentors found. Invite mentors in the Sthitih Mentors Directory first.
                  </div>
                ) : (
                  allGlobalMentors
                    .filter(m => {
                      const q = mentorAssignSearch.toLowerCase();
                      return (
                        (m.display_name || '').toLowerCase().includes(q) ||
                        (m.company || '').toLowerCase().includes(q) ||
                        (m.email || '').toLowerCase().includes(q) ||
                        (m.mentorship_areas || []).some((a: string) => a.toLowerCase().includes(q))
                      );
                    })
                    .map((m) => {
                      const isAssigned = selectedMentorIdsToAssign.includes(m.id);
                      return (
                        <div
                          key={m.id}
                          onClick={() => handleToggleMentorAssignment(m.id)}
                          className={`flex items-center justify-between p-2.5 rounded-xl border transition-all cursor-pointer ${
                            isAssigned
                              ? 'bg-[#DFF5E7] border-[#1F7A4F]/30 text-[#1F7A4F]'
                              : 'bg-[#FFFDF9] border-[#E4D8C8] text-[#243238] hover:border-[#DDD0BE]'
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <input
                              type="checkbox"
                              checked={isAssigned}
                              onChange={() => {}}
                              className="rounded border-[#DDD0BE] text-[#1F5A43] focus:ring-[#1F5A43]"
                            />
                            <div>
                              <span className="text-xs font-bold block">{m.display_name}</span>
                              <span className="text-[10px] text-[#6F706C]">
                                {m.designation || 'Mentor'} {m.company ? `• ${m.company}` : ''}
                              </span>
                            </div>
                          </div>

                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            isAssigned ? 'bg-[#1F7A4F] text-white' : 'bg-[#F7F2EA] text-[#8D8A83]'
                          }`}>
                            {isAssigned ? 'Granted' : 'Revoked'}
                          </span>
                        </div>
                      );
                    })
                )}
              </div>
            </div>

            <div className="pt-3 border-t border-[#E4D8C8] flex items-center justify-between">
              <span className="text-xs text-[#6F706C] font-semibold">
                {selectedMentorIdsToAssign.length} mentor(s) selected
              </span>

              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => setIsAssignMentorModalOpen(false)}
                  className="px-4 py-2 rounded-xl border border-[#DDD0BE] text-xs font-bold text-[#6F706C] hover:bg-[#F7F2EA]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveAssignedMentors}
                  disabled={assigningMentors}
                  className="px-5 py-2 rounded-xl bg-[#1F5A43] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#184735] disabled:opacity-50 flex items-center gap-2"
                >
                  {assigningMentors ? (
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Delete and Revoke Actions */}
      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        actionType={confirmModal.actionType}
        targetName={confirmModal.targetName}
      />
    </div>
  );
};

export default TenantDetail;
